import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, App } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const EditProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { t } = useTranslation();

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t('editprofile.update_success'));
        navigate('/profile');
      } else {
        message.error(data.error || t('editprofile.update_failed'));
      }
    } catch (error) {
      message.error(t('editprofile.network_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values: {
    currentPassword: string;
    newPassword: string;
  }) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t('editprofile.change_password_success'));
        form.resetFields(['currentPassword', 'newPassword', 'confirmPassword']);
      } else {
        message.error(data.error || t('editprofile.change_password_failed'));
      }
    } catch (error) {
      message.error(t('editprofile.network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <Title level={2}>{t('editprofile.title')}</Title>

      <Card title={t('editprofile.basic_info')} style={{ marginBottom: 24 }}>
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          initialValues={{
            username: user?.username || '',
            email: user?.email || '',
          }}
        >
          <Form.Item
            name="username"
            label={t('editprofile.username_label')}
            rules={[
              { required: true, message: t('editprofile.username_required') },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('editprofile.username_placeholder')}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t('editprofile.email_label')}
            rules={[
              { required: true, message: t('editprofile.email_required') },
              { type: 'email', message: t('editprofile.email_invalid') },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t('editprofile.email_placeholder')}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('editprofile.save_changes')}
              </Button>
              <Button onClick={() => navigate('/profile')}>
                {t('editprofile.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card title={t('editprofile.change_password')}>
        <Form onFinish={handleChangePassword} layout="vertical">
          <Form.Item
            name="currentPassword"
            label={t('editprofile.current_password')}
            rules={[
              {
                required: true,
                message: t('editprofile.current_password_required'),
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('editprofile.current_password_placeholder')}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('editprofile.new_password')}
            rules={[
              {
                required: true,
                message: t('editprofile.new_password_required'),
              },
              { min: 6, message: t('editprofile.new_password_min') },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('editprofile.new_password_placeholder')}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('editprofile.confirm_password')}
            dependencies={['newPassword']}
            rules={[
              {
                required: true,
                message: t('editprofile.confirm_password_required'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t('editprofile.confirm_password_mismatch'))
                  );
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('editprofile.confirm_password_placeholder')}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t('editprofile.change_password')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default EditProfilePage;
